import { sendRequest } from '@/lib/sendRequest';
import type { User, Venue } from '@/lib/UserFlow';
import type { SubmitUserResponse } from '@/app/api/user/submit/route';
import type { ServerTimeResponse } from '@/app/api/server-time/route';

/**
 * Submit user data from workflow state by calling the REST API.
 * This replaces the server action to avoid action ID mismatch issues after rebuild/redeploy.
 *
 * Only the venue's URL identifier is sent; the server re-resolves the
 * authoritative venue bundle so the client can't forge venue provenance.
 */
export async function submitUserFromWorkflow(user: User, venue: Venue | null): Promise<string> {
  const response = await sendRequest<SubmitUserResponse>('/api/user/submit', {
    method: 'post',
    body: { user, venueUrlIdentifier: venue?.urlIdentifier ?? null },
  });

  if (!response.success || !response.body?.success) {
    const errorMessage = response.body?.error || `API call failed with status ${response.status}`;
    throw new Error(errorMessage);
  }

  if (!response.body.userUid) {
    throw new Error('API response missing userUid');
  }

  return response.body.userUid;
}

/**
 * Fetch the server's current time as an ISO 8601 string. Used to stamp flow
 * moments (NFC tap, consent response) with a trustworthy clock instead of the
 * kiosk clock.
 */
export async function fetchServerTime(): Promise<string> {
  const response = await sendRequest<ServerTimeResponse>('/api/server-time', { method: 'post' });

  if (!response.success || !response.body?.serverTime) {
    throw new Error(`Server time request failed with status ${response.status}`);
  }

  return response.body.serverTime;
}
